// Central WhatsApp + services configuration for Velixa Health.
// Every service tile / card / link / button pulls from this file.
// Adding a service here automatically wires up its WhatsApp link.

const WA_NUMBER = "917483985244";

export const WA_BASE = `https://wa.me/${WA_NUMBER}`;

// Build a wa.me URL from a message, URL-encoded via encodeURIComponent.
export const buildWaLink = (message) =>
  `${WA_BASE}?text=${encodeURIComponent(message)}`;

// Message template for a specific service.
export const serviceMessage = (serviceName) =>
  `Hi Velixa Health, I'm interested in ${serviceName}. Can I get more information about this service?\nPatient's area in Bangalore: \nPatient's age: `;

// Shared / special messages
export const GENERAL_MESSAGE = "Hi Velixa Health, I need help with home care for my family member. Can you guide me?";
export const FAMILIES_ABROAD_MESSAGE = "Hi Velixa Health, my parents live in Bangalore and I live abroad / in another city. I'd like to know how you can take care of them at home.";
export const blogMessage = (title) => `Hi Velixa Health, I just read your article '${title}' and I'd like to know more about your services.`;

// -------- Service groups --------
export const SERVICE_GROUPS = [
  {
    key: "home-nursing",
    title: "Home Nursing",
    short: "Nurse-led clinical care at home — 12h, 24h, post-op and specialist.",
    image: "https://images.unsplash.com/photo-1559599189-fe84dea4eb79?fm=jpg&q=80&w=1400&auto=format&fit=crop",
    icon: "HeartPulse",
  },
  {
    key: "caretakers",
    title: "Caretakers & Attendants",
    short: "Compassionate attendants for daily care, mobility & companionship.",
    image: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?fm=jpg&q=80&w=1400&auto=format&fit=crop",
    icon: "HandHeart",
  },
  {
    key: "checkups",
    title: "Health Checkups at Home",
    short: "Full-body, cardiac, ECG and lab tests — collected at your doorstep.",
    image: "https://images.unsplash.com/photo-1551076805-e1869033e561?fm=jpg&q=80&w=1400&auto=format&fit=crop",
    icon: "Stethoscope",
  },
];

// -------- All services (ordered) --------
// icon = lucide-react icon name
// slug = URL slug for /services/<slug> (Phase 2)
export const SERVICES = [
  // Home Nursing
  { name: "12-Hour Home Nurse", slug: "12-hour-home-nurse-bangalore", group: "home-nursing", icon: "Clock", desc: "Day or night 12-hour shift at home.", clinical: true },
  { name: "24-Hour Home Nurse", slug: "24-hour-home-nurse-bangalore", group: "home-nursing", icon: "Clock12", desc: "Round-the-clock trained nurse at home.", clinical: true },
  { name: "Post-Hospital Discharge Care", slug: "post-hospital-discharge-care-bangalore", group: "home-nursing", icon: "Hospital", desc: "Smooth recovery after hospital discharge.", clinical: true },
  { name: "Post-Surgery Care", slug: "post-surgery-care-bangalore", group: "home-nursing", icon: "Stethoscope", desc: "Wound, pain and mobility care after surgery.", clinical: true },
  { name: "Stroke Care", slug: "stroke-care-at-home-bangalore", group: "home-nursing", icon: "Brain", desc: "Paralysis & stroke recovery nursing.", clinical: true },
  { name: "Elderly & Bedridden Patient Care", slug: "bedridden-patient-care-bangalore", group: "home-nursing", icon: "BedDouble", desc: "Positioning, hygiene & bedsore prevention.", clinical: true },
  { name: "IV Medication & Injections", slug: "iv-injection-at-home-bangalore", group: "home-nursing", icon: "Syringe", desc: "Doctor-prescribed IV & injections at home.", clinical: true, note: "Only as per doctor's advice or valid prescription." },
  { name: "Wound Care & Dressing", slug: "wound-dressing-at-home-bangalore", group: "home-nursing", icon: "Bandage", desc: "Sterile dressings for post-op & diabetic wounds.", clinical: true },
  { name: "Tracheostomy Care", slug: "tracheostomy-care-at-home-bangalore", group: "home-nursing", icon: "Activity", desc: "Suctioning, cleaning & tube care.", clinical: true },
  { name: "Catheter & Foley Care", slug: "catheter-care-at-home-bangalore", group: "home-nursing", icon: "Droplets", desc: "Foley insertion, change & hygiene.", clinical: true },

  // Caretakers & Attendants
  { name: "12-Hour Caretaker (Day / Night)", slug: "12-hour-caretaker-bangalore", group: "caretakers", icon: "Sun", desc: "Day or night 12-hour attendant." },
  { name: "24-Hour Caretaker / Patient Attendant", slug: "24-hour-caretaker-bangalore", group: "caretakers", icon: "Moon", desc: "Round-the-clock attendant." },
  { name: "Elderly Care at Home", slug: "elderly-care-at-home-bangalore", group: "caretakers", icon: "HandHeart", desc: "Mobility, meals & companionship." },

  // Health Checkups
  { name: "Complete Health Checkup at Home", slug: "complete-health-checkup-at-home-bangalore", group: "checkups", icon: "ClipboardList", desc: "Full assessment + lab + doctor consult.", clinical: true },
  { name: "Cardiac Health Checkup at Home", slug: "cardiac-checkup-at-home-bangalore", group: "checkups", icon: "HeartPulse", desc: "History, assessment, blood test, doctor + dietitian.", clinical: true },
  { name: "Preventive Health Screening", slug: "preventive-health-screening-bangalore", group: "checkups", icon: "ShieldCheck", desc: "Catch issues early with regular screening.", clinical: true },
  { name: "Home ECG", slug: "home-ecg-bangalore", group: "checkups", icon: "Activity", desc: "12-lead ECG at home with digital report.", clinical: true },
  { name: "Blood Sample Collection at Home", slug: "blood-sample-collection-at-home-bangalore", group: "checkups", icon: "FlaskConical", desc: "Fasting-friendly collection at your doorstep.", clinical: true },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);

export const servicesByGroup = (groupKey) => SERVICES.filter((s) => s.group === groupKey);

// Fire a GA4 whatsapp_click event (safe no-op if gtag not loaded).
export const trackWhatsApp = (serviceName) => {
  if (typeof window === "undefined") return;
  const payload = { service_name: serviceName, page_url: window.location.href };
  try {
    if (typeof window.gtag === "function") {
      window.gtag("event", "whatsapp_click", payload);
    }
    // Also push to dataLayer in case GTM is used.
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: "whatsapp_click", ...payload });
  } catch {
    /* noop */
  }
};

// Hero quick-pick chips: 4 common entry points.
export const QUICK_CHIPS = [
  { label: "Home Nurse", service: "Home Nurse" },
  { label: "Caretaker", service: "Caretaker / Patient Attendant" },
  { label: "Post-Discharge Care", service: "Post-Hospital Discharge Care" },
  { label: "Health Checkup at Home", service: "Health Checkup at Home" },
];
